import { G } from '../querystring';

interface columnSearch {
    column: string;
    searches: string[];
}

// export const TokenType = {
//     TEXT: '',
//     COLUMN_SEARCH,
// } as const;
// export type StockItemID = typeof StockItemID[keyof typeof StockItemID];

export const TokenType = {
    TEXT: 1,
    COLUMN_SEARCH: 2,
} as const;
export type TokenType = typeof TokenType[keyof typeof TokenType];

export interface Token {
    type: TokenType;
    text?: string;
    columnSearch?: columnSearch;
}

export class Query {
    private tokens: Token[] = [];
    private inputTokens: Token[] = [];
    private defaultTokens: Token[] = [];

    constructor(public searchInput: HTMLInputElement, public SearchForm: HTMLFormElement) {
        this.updateTokensFromSearch();

        this.searchInput.addEventListener('submit', () => this.updateTokensFromSearch());
    }

    Search() {
        this.SearchForm.dispatchEvent(new CustomEvent('changed.iap.tokens'));
    }

    private updateSearchInput(filter: string, filterLower: string, submit: boolean = true) {
        this.removeFilter(filterLower, this.inputTokens);

        const filterTokens = this.getFilterTokens(filterLower);
        const defaultFilterTokens = this.getFilterTokens(filterLower, this.defaultTokens);

        if (defaultFilterTokens.length && !filterTokens.length) {
            this.addFilter(filter, 'all', this.inputTokens);
        } else if (!this.columnSearchesEqual(filterTokens.map(t => t.columnSearch!), defaultFilterTokens.map(t => t.columnSearch!))) {
            for (const t of filterTokens) {
                this.inputTokens.push(this.cloneToken(t));
            }
        }

        const oldSearch = this.searchInput.value;
        this.searchInput.value = this.String(this.inputTokens).trim();
        if (submit && this.searchInput.value !== oldSearch) {
            this.Search();
        }
    }

    private updateTokensFromSearch() {
        this.inputTokens = this.Parse(G().Get('q') || '');
        this.tokens = [];
        for (const t of this.inputTokens) {
            if (this.isAllToken(t)) {
                continue;
            }
            this.tokens.push(this.cloneToken(t));
        }
        this.addDefaultTokens();
    }

    private addDefaultTokens() {
        for (const t of this.defaultTokens) {
            if (t.type === TokenType.TEXT) {
                // tslint:disable-next-line: no-console
                console.warn(`default text filters are ignored: tried to give '${t.text ?? 'undefined'}' as a filter`);
                continue;
            }

            if (this.hasFilter(t.columnSearch!.column.toLowerCase(), this.inputTokens)) {
                continue;
            }

            this.tokens.push(this.cloneToken(t));
        }
    }

    private addFilter(filter: string, search: string, tokens: Token[] = this.tokens) {
        tokens.push({
            type: TokenType.COLUMN_SEARCH,
            columnSearch: {
                column: filter,
                searches: [search],
            },
        });
    }

    AddFilter(filter: string, search: string) {
        this.addFilter(filter, search);
        this.updateSearchInput(filter, filter.toLowerCase());
    }

    private removeFilter(filterLower: string, tokens: Token[] = this.tokens) {
        for (let i = 0; i < tokens.length; i++) {
            const t = tokens[i];
            if (t.type === TokenType.COLUMN_SEARCH && t.columnSearch!.column.toLowerCase() === filterLower) {
                tokens.splice(i, 1);
                // don't break here, we want to remove *ALL* filters of this type
                // break
            }
        }
    }

    RemoveFilter(filter: string, submit: boolean = true) {
        const filterLower = filter.toLowerCase();
        this.removeFilter(filterLower);

        this.updateSearchInput(filter, filterLower, submit);
    }

    private hasFilter(filterLower: string, tokens: Token[] = this.tokens): boolean {
        for (const t of tokens) {
            if (t.type === TokenType.COLUMN_SEARCH && t.columnSearch!.column.toLowerCase() === filterLower) {
                return true;
            }
        }

        return false;
    }

    private getFilterTokens(filterLower: string, tokens: Token[] = this.tokens): Token[] {
        const filterTokens = [];

        for (const t of tokens) {
            if (t.type === TokenType.COLUMN_SEARCH && t.columnSearch!.column.toLowerCase() === filterLower) {
                filterTokens.push(t);
            }
        }

        return filterTokens;
    }

    private hasSearch(filterLower: string, searchLower: string, filterTokens = this.getFilterTokens(filterLower)): boolean {
        for (const t of filterTokens) {
            for (const s of t.columnSearch!.searches) {
                if (s.toLowerCase() === searchLower) {
                    return true;
                }
            }
        }

        return false;
    }

    HasSearch(filter: string, search: string): boolean {
        return this.hasSearch(filter.toLowerCase(), search.toLowerCase());
    }

    private addSearch(filter: string, filterLower: string, search: string, searchLower: string, tokens: Token[] = this.tokens, filterTokens = this.getFilterTokens(filterLower, tokens)) {
        if (filterTokens.length && !this.hasSearch(filterLower, searchLower, filterTokens)) {
            filterTokens[filterTokens.length - 1].columnSearch!.searches.push(search);
        } else {
            this.addFilter(filter, search);
        }
    }

    AddSearch(filter: string, search: string) {
        const filterLower = filter.toLowerCase();
        this.addSearch(filter, filterLower, search, search.toLowerCase());

        this.updateSearchInput(filter, filterLower);
    }

    private removeSearch(filterLower: string, searchLower: string, filterTokens = this.getFilterTokens(filterLower)) {
        for (const t of filterTokens) {
            for (let i = 0; i < t.columnSearch!.searches.length; i++) {
                const s = t.columnSearch!.searches[i];
                if (s.toLowerCase() === searchLower) {
                    t.columnSearch!.searches.splice(i, 1);
                }
            }
            if (!t.columnSearch!.searches.length) {
                this.tokens.splice(this.tokens.indexOf(t), 1);
            }
        }
    }

    RemoveSearch(filter: string, search: string) {
        const filterLower = filter.toLowerCase();
        this.removeSearch(filterLower, search.toLowerCase());

        this.updateSearchInput(filter, filterLower);
    }

    SetFilterWithSubmit(filter: string, submit: boolean, ...searches: string[]) {
        const filterLower = filter.toLowerCase();
        this.removeFilter(filterLower);
        for (const s of searches) {
            this.addSearch(filter, filterLower, s, s.toLowerCase());
        }

        this.updateSearchInput(filter, filterLower, submit);
    }

    SetFilter(filter: string, ...searches: string[]) {
        return this.SetFilterWithSubmit(filter, true, ...searches);
    }

    private columnSearchesEqual(a: columnSearch[], b: columnSearch[]): boolean {
        if (a.length !== b.length) {
            return false;
        }

        const _a = [...a.map(v => { v.searches.sort(); return JSON.stringify(v).toLowerCase(); })];
        const _b = [...b.map(v => { v.searches.sort(); return JSON.stringify(v).toLowerCase(); })];

        _a.sort();
        _b.sort();

        return _a.join() === _b.join();
    }

    SetDefaultQuery(q: string) {
        this.defaultTokens = this.Parse(q);
        this.addDefaultTokens();
    }

    private cloneToken(t: Token): Token {
        switch (t.type) {
            case TokenType.COLUMN_SEARCH:
                return {
                    type: TokenType.COLUMN_SEARCH,
                    columnSearch: {
                        column: t.columnSearch!.column,
                        searches: [...t.columnSearch!.searches],
                    },
                };
            case TokenType.TEXT:
                return {
                    type: TokenType.TEXT,
                    text: t.text,
                };
        }
    }

    ResetFilter(filter: string) {
        const filterLower = filter.toLowerCase();
        this.removeFilter(filterLower);
        for (const t of this.getFilterTokens(filterLower, this.defaultTokens)) {
            this.tokens.push(this.cloneToken(t));
        }
        this.updateSearchInput(filter, filterLower);
    }

    FilterIsDefault(filter: string): boolean {
        const filterLower = filter.toLowerCase();

        const filterTokens = this.getFilterTokens(filterLower);
        const defaultFilterTokens = this.getFilterTokens(filterLower, this.defaultTokens);

        return this.columnSearchesEqual(filterTokens.map(t => t.columnSearch!), defaultFilterTokens.map(t => t.columnSearch!));
    }

    GetFilterSearches(filter: string): string[] {
        const searches: string[] = [];
        for (const t of this.getFilterTokens(filter.toLowerCase())) {
            searches.push(...t.columnSearch!.searches);
        }
        return searches;
    }

    GetCurrentFilterNames(): string[] {
        const filterTokens: string[] = [];

        for (const t of this.tokens) {
            if (t.type === TokenType.COLUMN_SEARCH) {
                filterTokens.push(t.columnSearch!.column);
            }
        }

        return filterTokens;
    }

    UsingDefaultFilters(): boolean {
        for (const f of this.GetCurrentFilterNames()) {
            if (this.FilterIsDefault(f)) {
                return true;
            }
        }

        return false;
    }

    HasTextSearch(): boolean {
        for (const t of this.inputTokens) {
            if (t.type === TokenType.TEXT) {
                return true;
            }
        }

        return false;
    }

    private isAllToken(t: Token): boolean {
        return t.type === TokenType.COLUMN_SEARCH && t.columnSearch!.searches.length === 1 && t.columnSearch!.searches[0].toLowerCase() === 'all';
    }

    private isColumnChar(c: string): boolean {
        return ('A' <= c && c <= 'Z') || ('a' <= c && c <= 'z') || ('0' <= c && c <= '9');
    }

    private isQuoteChar(c: string): boolean {
        return c === '"' || c === '\'' || c === '`';
    }

    private isWhitespaceChar(c: string): boolean {
        return /\s/.test(c);
    }

    toString(): string {
        return this.String(this.tokens);
    }

    Parse(s: string): Token[] {
        const tokens: Token[] = [];

        const l = s.length;
        let i = 0;
        let start = 0;

        const nextN = (n: number) => i += n;

        const next = () => i++;

        const prev = () => i--;

        const consumeUntilSpaceOrComma = () => {
            while (i < l) {
                if (/\s/.test(s[i]) || s[i] === ',') {
                    break;
                }
                next();
            }
            prev();
            if (i >= l) {
                i = l - 1;
            }
        };

        const consumeUntilQuote = (q: string) => {
            while (i < l) {
                if (s[i] === q) {
                    break;
                }
                next();
            }
            prev();
            if (i >= l) {
                i = l - 1;
            }
        };

        const consumeAllColumnChar = () => {
            next();
            while (i < l) {
                if (this.isColumnChar(s[i])) {
                    next();
                } else {
                    prev();
                    break;
                }
            }
            if (i >= l) {
                i = l - 1;
            }
        };

        let currentToken: Token;

        const pushText = () => {
            if (!(currentToken && currentToken.type === TokenType.TEXT)) {
                currentToken = {
                    type: TokenType.TEXT,
                    text: '',
                };
                tokens.push(currentToken);
            }

            currentToken.text += s.substring(start, i + 1);
        };

        let currentColumn: columnSearch;

        const pushTokenColumn = () => {
            currentColumn = {
                column: s.substring(start, i + 1),
                searches: [],
            };
            currentToken = {
                type: TokenType.COLUMN_SEARCH,
                columnSearch: currentColumn,
            };
            tokens.push(currentToken);
        };

        const pushTokenValue = () => {
            currentColumn.searches.push(s.substring(start, i + 1));
        };

        while (i < l) {
            switch (true) {
                case this.isColumnChar(s[i]):
                    start = i;
                    consumeAllColumnChar();

                    if (i + 1 < l && s[i + 1] === ':') {
                        pushTokenColumn();
                        nextN(2);

                        while (i < l && !this.isWhitespaceChar(s[i])) {
                            start = i;

                            const unquoted = !this.isQuoteChar(s[i]);
                            if (unquoted) {
                                consumeUntilSpaceOrComma();
                            } else {
                                const q = s[i];
                                next();
                                start = i;
                                consumeUntilQuote(q);
                            }

                            pushTokenValue();
                            next();
                            if (!unquoted) {
                                next();
                            }

                            if (i < l) {
                                if (s[i] === ',') {
                                    next();
                                } else {
                                    break;
                                }
                            }
                        }
                    } else {
                        pushText();
                    }
                    break;
                default:
                    start = i;
                    pushText();
            }

            next();
        }

        return tokens;
    }

    String(tokens: Token[]): string {
        let q = '';
        for (let i = 0; i < tokens.length; i++) {
            if (i) {
                q += ' ';
            }

            const t = tokens[i];

            switch (t.type) {
                case TokenType.TEXT:
                    q += t.text!.trim();
                    break;
                case TokenType.COLUMN_SEARCH:
                    const c = t.columnSearch!;

                    q += `${c.column.trim()}:`;
                    for (let i = 0; i < c.searches.length; i++) {
                        const s = c.searches[i];

                        if (i) {
                            q += ',';
                        }
                        const quoted = s.indexOf(' ') !== -1;
                        if (quoted) {
                            q += '"';
                        }
                        q += s.trim();
                        if (quoted) {
                            q += '"';
                        }
                    }
                    break;
            }
        }

        return q.trim();
    }

    Set(q: string, submit: boolean = true) {
        this.searchInput.value = q;
        if (submit) {
            this.Search();
        } else {
            this.updateTokensFromSearch();
        }
    }

    Reset() {
        this.Set('');
    }
}